import { useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { brandAssets } from "@/lib/brand/tokens";
import { getSetupFlow } from "@/data/setup/registry";
import { deriveDeskIdentity } from "@/lib/setup/deskIdentity";
import { useSubmitSetupRequest } from "@/hooks/setup/useSetupRequest";
import type { SetupFlowAnswers, SetupFlowDefinition, SetupQuestion } from "@/types/setup/flow";
import type { SetupRequestField } from "@/types/setup/request";
import SlideShell, { Lead, Title } from "@/pages/Tour/components/SlideShell";
import { SetupFieldSlide } from "./components/SetupFieldSlide";
import { GenieFrontDeskPreview } from "./components/GenieFrontDeskPreview";

type Phase = "intro" | "questions" | "finale";

const SetupFlowPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const flow = getSetupFlow(slug);
  const submit = useSubmitSetupRequest();
  const [phase, setPhase] = useState<Phase>("intro");
  const [stepIndex, setStepIndex] = useState(0);

  const { register, trigger, watch, getValues } = useForm<SetupFlowAnswers>({
    mode: "onChange",
    defaultValues: flow ? readStoredDraft(flow.meta.slug, flow.questions) : {},
  });

  // Subscribe to every field so the living preview reacts as they type.
  const answers = watch();

  if (!flow) {
    return <Navigate to="/setup" replace />;
  }

  const questions = flow.questions;
  const activeQuestion = questions[stepIndex];
  const isLastStep = stepIndex === questions.length - 1;
  const identity = deriveDeskIdentity(answers.website);

  const buildRegisterProps = (question: SetupQuestion) =>
    register(question.id, {
      required: question.requiredMessage,
      validate: (value) => (question.validate ? question.validate(value ?? "", getValues()) : true),
    });

  const submitRequest = async (values: SetupFlowAnswers) => {
    const fields: SetupRequestField[] = questions.map((question) => ({
      label: question.label,
      value: (values[question.id] || "").trim(),
    }));

    await submit.mutateAsync({
      flowSlug: flow.meta.slug,
      flowName: flow.meta.name,
      fields,
    });

    clearStoredDraft(flow.meta.slug);
    const contact = values.contactName?.trim();
    const email = values.email?.trim();
    const params = new URLSearchParams();
    if (contact) params.set("n", contact);
    if (email) params.set("e", email);
    navigate(`/setup/${flow.meta.slug}/done?${params.toString()}`);
  };

  const begin = () => {
    setStepIndex(0);
    setPhase("questions");
  };

  const advanceQuestion = async () => {
    const isValid = await trigger(activeQuestion.id);
    if (!isValid) return;
    writeStoredDraft(flow.meta.slug, watch());
    if (isLastStep) {
      setPhase("finale");
      return;
    }
    setStepIndex((current) => Math.min(current + 1, questions.length - 1));
  };

  const goBack = () => {
    if (phase === "intro") {
      navigate("/setup");
      return;
    }
    if (phase === "finale") {
      setStepIndex(questions.length - 1);
      setPhase("questions");
      return;
    }
    if (stepIndex === 0) {
      setPhase("intro");
      return;
    }
    setStepIndex((current) => Math.max(current - 1, 0));
  };

  if (submit.isPending) {
    return <WakingState flow={flow} identity={identity?.name} />;
  }

  const total = questions.length + 2;
  const pageIndex = phase === "intro" ? 0 : phase === "finale" ? questions.length + 1 : stepIndex + 1;
  const revealedCount =
    phase === "intro" ? 0 : phase === "finale" ? questions.length : stepIndex + 1;
  const finaleTitle = identity ? `Meet ${identity.name}'s front desk.` : flow.finale.title;
  const slideKey = phase === "questions" ? `q-${stepIndex}` : phase;

  return (
    <div className="fixed inset-0 overflow-hidden bg-[#F9FAFC]">
      <AmbientOrbs />

      <div key={slideKey} className="absolute inset-0" style={{ animation: "fadeInUp 0.5s ease-out" }}>
        {phase === "intro" && (
          <Cover
            intro={flow.intro}
            badge={flow.meta.setupMinutes}
            onStart={begin}
            onBrowse={() => navigate("/setup")}
          />
        )}

        {phase === "questions" && (
          <SlideShell eyebrow={activeQuestion.eyebrow} page={stepIndex + 2} total={total}>
            <form
              className="grid h-full items-center gap-8 lg:grid-cols-2 lg:gap-14"
              onSubmit={(event) => {
                event.preventDefault();
                void advanceQuestion();
              }}
            >
              <div className="space-y-5">
                <Title>{activeQuestion.title}</Title>
                <Lead>{activeQuestion.subtitle}</Lead>

                <div className="pt-1">
                  <SetupFieldSlide
                    question={activeQuestion}
                    registerProps={buildRegisterProps(activeQuestion)}
                    flat
                  />
                </div>

                <div className="flex items-center gap-3 pt-1">
                  <Button type="button" variant="outline" onClick={goBack} className="h-11 rounded-xl">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="h-11 flex-1 rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white shadow-lg shadow-genie-primary/25"
                  >
                    {isLastStep ? "Almost there" : "Continue"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>

                <p className="text-xs text-gray-500">
                  No account needed — our team builds your genie and gets back to you, usually within
                  a few hours.
                </p>
              </div>

              <div className="flex justify-center lg:justify-end">
                <GenieFrontDeskPreview
                  flow={flow}
                  answers={answers}
                  revealedCount={revealedCount}
                  activeSlot={activeQuestion.deskSlot}
                />
              </div>
            </form>
          </SlideShell>
        )}

        {phase === "finale" && (
          <Finale
            finale={flow.finale}
            title={finaleTitle}
            mascot={flow.meta.mascot}
            onSubmit={() => void submitRequest(getValues())}
            onBack={goBack}
          />
        )}
      </div>

      <BottomProgress index={pageIndex} total={total} />
    </div>
  );
};

// ─── Cover (intro) — full-bleed, cinematic ──────────────────────────────────
const Cover = ({
  intro,
  badge,
  onStart,
  onBrowse,
}: {
  intro: SetupFlowDefinition["intro"];
  badge: string;
  onStart: () => void;
  onBrowse: () => void;
}) => (
  <div className="relative h-full w-full overflow-hidden">
    <img src={intro.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0" style={{ background: COVER_SCRIM }} />
    <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: RIBBON }} />

    {/* Top row */}
    <div className="absolute inset-x-6 top-6 z-10 flex items-start justify-between sm:inset-x-12 sm:top-10">
      <img
        src={brandAssets.wordmark}
        alt="Help Genie"
        className="h-8 w-auto sm:h-10"
        style={{ filter: "brightness(0) invert(1) drop-shadow(0 2px 8px rgba(0,0,0,0.45))" }}
      />
      <span className="rounded-full border border-white/60 bg-white/15 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white backdrop-blur sm:text-xs">
        {badge}
      </span>
    </div>

    {/* Bottom content */}
    <div className="absolute inset-x-6 bottom-24 z-10 max-w-4xl sm:inset-x-12 sm:bottom-28">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/85">{intro.eyebrow}</p>
      <h1
        className="mt-4 text-4xl font-extrabold leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl"
        style={{ textShadow: "0 2px 18px rgba(0,0,0,0.45)" }}
      >
        {intro.title}
      </h1>
      <div className="mt-5 h-1.5 w-32 rounded-full" style={{ background: RIBBON }} />
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/90 sm:text-lg">
        {intro.subtitle}
      </p>
      <div className="mt-7 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
        <button
          onClick={onStart}
          className="inline-flex items-center gap-2 rounded-xl px-7 py-3.5 font-semibold text-white shadow-lg transition-all hover:scale-105 hover:opacity-95"
          style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
        >
          {intro.ctaLabel}
          <ArrowRight className="h-4 w-4" />
        </button>
        <span className="text-xs text-white/80">{intro.reassurance}</span>
      </div>
      <button
        onClick={onBrowse}
        className="mt-5 text-xs font-medium text-white/70 underline-offset-4 hover:text-white hover:underline"
      >
        Browse all setups
      </button>
    </div>
  </div>
);

// ─── Finale — cinematic payoff, the genie's confident entrance ───────────────
const Finale = ({
  finale,
  title,
  mascot,
  onSubmit,
  onBack,
}: {
  finale: SetupFlowDefinition["finale"];
  title: string;
  mascot: string;
  onSubmit: () => void;
  onBack: () => void;
}) => (
  <div className="relative h-full w-full overflow-hidden">
    <img src={finale.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0" style={{ background: FINALE_SCRIM }} />
    <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: RIBBON }} />

    <img
      src={brandAssets.wordmark}
      alt="Help Genie"
      className="absolute left-6 top-6 z-10 h-8 w-auto sm:left-12 sm:top-10 sm:h-10"
      style={{ filter: "brightness(0) invert(1) drop-shadow(0 2px 8px rgba(0,0,0,0.45))" }}
    />

    {/* Genie makes its entrance, bottom-right, fully visible */}
    <img
      src={mascot}
      alt=""
      className="pointer-events-none absolute -bottom-4 right-0 z-0 w-40 object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)] sm:right-6 sm:w-56 lg:w-64 animate-glow"
    />

    <div className="absolute inset-0 z-10 flex items-center">
      <div className="max-w-2xl px-6 sm:px-12">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/85">
          {finale.eyebrow}
        </p>
        <h1
          className="mt-4 text-4xl font-extrabold leading-[1.0] tracking-tight text-white sm:text-5xl lg:text-6xl"
          style={{ textShadow: "0 2px 18px rgba(0,0,0,0.45)" }}
        >
          {title}
        </h1>
        <p className="mt-5 max-w-xl text-base leading-relaxed text-white/90 sm:text-lg">
          {finale.subtitle}
        </p>
        <div className="mt-7 flex items-center gap-3">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 rounded-xl border border-white/40 bg-white/10 px-5 py-3 text-sm font-semibold text-white backdrop-blur transition-all hover:bg-white/20"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <button
            onClick={onSubmit}
            className="inline-flex items-center gap-2 rounded-xl px-7 py-3.5 font-semibold text-white shadow-lg transition-all hover:scale-105 hover:opacity-95"
            style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
          >
            {finale.ctaLabel}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-4 text-xs text-white/75">{finale.reassurance}</p>
      </div>
    </div>
  </div>
);

const BottomProgress = ({ index, total }: { index: number; total: number }) => (
  <div className="absolute inset-x-0 bottom-0 z-20 px-6 pb-4 sm:px-10 sm:pb-5">
    <div className="h-1 overflow-hidden rounded-full bg-white/25 backdrop-blur">
      <div
        className="h-full rounded-full transition-all duration-500 ease-out"
        style={{ width: `${((index + 1) / total) * 100}%`, background: RIBBON }}
      />
    </div>
  </div>
);

const AmbientOrbs = () => (
  <div className="pointer-events-none absolute inset-0">
    <div
      className="absolute -left-40 -top-40 h-[60vw] w-[60vw] rounded-full opacity-25 blur-3xl"
      style={{ background: "radial-gradient(closest-side, #4E9CFF, transparent)" }}
    />
    <div
      className="absolute -bottom-40 -right-40 h-[60vw] w-[60vw] rounded-full opacity-25 blur-3xl"
      style={{ background: "radial-gradient(closest-side, #FF4DB8, transparent)" }}
    />
  </div>
);

const WakingState = ({ flow, identity }: { flow: SetupFlowDefinition; identity?: string }) => (
  <div className="fixed inset-0 overflow-hidden">
    <img src={flow.finale.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0" style={{ background: FINALE_SCRIM }} />
    <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: RIBBON }} />
    <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center">
      <div className="mb-6 flex items-center gap-2">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="h-2.5 w-2.5 animate-gentle-pulse rounded-full bg-white"
            style={{ animationDelay: `${i * 0.22}s` }}
          />
        ))}
      </div>
      <h1
        className="text-3xl font-extrabold text-white sm:text-4xl"
        style={{ textShadow: "0 2px 18px rgba(0,0,0,0.45)" }}
      >
        Waking up {identity ? `${identity}'s` : "your"} front desk…
      </h1>
      <p className="mt-3 max-w-md text-sm text-white/85 sm:text-base">
        We're handing your {flow.meta.name} details to the team that brings it to life.
      </p>
    </div>
  </div>
);

const RIBBON = "linear-gradient(90deg, #4E9CFF 0%, #D6409F 50%, #4E9CFF 100%)";

const COVER_SCRIM =
  "linear-gradient(180deg, rgba(15,24,52,0.35) 0%, rgba(15,24,52,0.04) 30%, rgba(15,24,52,0.18) 55%, rgba(15,24,52,0.90) 100%), linear-gradient(120deg, rgba(78,156,255,0.16) 0%, rgba(255,77,184,0.12) 55%, rgba(0,0,0,0) 100%)";

const FINALE_SCRIM =
  "linear-gradient(120deg, rgba(15,24,52,0.85) 0%, rgba(15,24,52,0.55) 45%, rgba(15,24,52,0.20) 100%), linear-gradient(180deg, rgba(15,24,52,0.30) 0%, rgba(15,24,52,0.55) 100%)";

function draftKey(slug: string): string {
  return `hg-setup-${slug}`;
}

function readStoredDraft(slug: string, questions: SetupQuestion[]): SetupFlowAnswers {
  const empty: SetupFlowAnswers = {};
  questions.forEach((question) => {
    empty[question.id] = "";
  });
  if (typeof window === "undefined") return empty;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(draftKey(slug)) || "{}");
    questions.forEach((question) => {
      if (typeof parsed[question.id] === "string") {
        empty[question.id] = parsed[question.id];
      }
    });
    return empty;
  } catch {
    return empty;
  }
}

function writeStoredDraft(slug: string, values: SetupFlowAnswers) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(draftKey(slug), JSON.stringify(values));
}

function clearStoredDraft(slug: string) {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(draftKey(slug));
}

export default SetupFlowPage;
